import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { HERO } from "../../data/content";
import { Portrait } from "./Portrait";

const wordReveal = {
    hidden: { y: "110%" },
    show: (i = 0) => ({
        y: 0,
        transition: {
            duration: 0.95,
            ease: [0.22, 1, 0.36, 1],
            delay: 0.15 + i * 0.06,
        },
    }),
};

const lineChildren = (text, base = 0) =>
    text.split(" ").map((w, i) => (
        <span key={`${w}-${i}`} className="reveal-word">
            <motion.span
                variants={wordReveal}
                custom={base + i}
                className="inline-block mr-[0.28em]"
            >
                {w}
            </motion.span>
        </span>
    ));

export const Hero = () => {
    const { scrollY } = useScroll();
    // Subtle parallax
    const portraitY = useTransform(scrollY, [0, 800], [0, -60]);
    const headingY = useTransform(scrollY, [0, 800], [0, -20]);

    return (
        <section
            id="hero"
            data-testid="hero-section"
            className="relative pt-32 md:pt-44 pb-16 md:pb-24 overflow-hidden"
        >
            {/* Background dot grid */}
            <div className="pointer-events-none absolute inset-0 dotgrid" />

            <div className="relative mx-auto max-w-[1400px] px-5 md:px-10">
                {/* Main grid — text + portrait side by side on desktop,
                    stacked on mobile with the photo below name + description */}
                <div className="grid grid-cols-12 gap-8 md:gap-12 items-center">
                    {/* Left — name + description */}
                    <motion.div
                        style={{ y: headingY }}
                        initial="hidden"
                        animate="show"
                        className="col-span-12 md:col-span-7"
                    >
                        <h1
                            data-testid="hero-name"
                            className="font-serif tracking-[-0.02em] leading-[0.88] text-[clamp(3.4rem,9vw,8.5rem)]"
                        >
                            <span className="block reveal-line-wrap">
                                <motion.span
                                    variants={wordReveal}
                                    custom={0}
                                    className="inline-block"
                                >
                                    {HERO.firstName}
                                </motion.span>
                            </span>
                            <span className="block reveal-line-wrap italic">
                                <motion.span
                                    variants={wordReveal}
                                    custom={1}
                                    className="inline-block"
                                >
                                    {HERO.lastName}
                                    <span className="text-[var(--brand)]">.</span>
                                </motion.span>
                            </span>
                        </h1>

                        <p className="mt-8 md:mt-10 font-serif text-xl md:text-3xl leading-[1.3] tracking-[-0.01em] text-[var(--ink)] max-w-xl">
                            {HERO.lines.map((l, li) => (
                                <span key={li} className="block">
                                    {lineChildren(l, 3 + li * 8)}
                                </span>
                            ))}
                        </p>
                    </motion.div>

                    {/* Right — portrait (renders after text in DOM, so it sits
                        below name + description on mobile) */}
                    <motion.div
                        style={{ y: portraitY }}
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                            duration: 1,
                            delay: 0.5,
                            ease: [0.22, 1, 0.36, 1],
                        }}
                        className="col-span-12 md:col-span-5"
                    >
                        <Portrait />
                    </motion.div>
                </div>

                {/* Download resume */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.1, duration: 0.6 }}
                    className="mt-12 md:mt-16 pt-8 border-t-2 border-[var(--ink)] flex flex-wrap items-center justify-between gap-6"
                >
                    <a
                        data-testid="download-resume"
                        href={HERO.resumeHref}
                        target={HERO.resumeHref.startsWith("http") ? "_blank" : undefined}
                        rel={HERO.resumeHref.startsWith("http") ? "noopener noreferrer" : undefined}
                        className="inline-flex items-center gap-3 group"
                    >
                        <span className="inline-flex items-center justify-center w-8 h-8 border-hard bg-[var(--surface)] group-hover:bg-[var(--brand)] group-hover:text-[var(--page)] group-hover:border-[var(--brand)] transition-colors">
                            <ArrowUpRight
                                className="w-4 h-4"
                                strokeWidth={2.4}
                            />
                        </span>
                        <span className="mono-label link-underline">
                            Résumé / CV
                        </span>
                    </a>
                    <span className="mono-meta flex items-center gap-2">
                        <span className="text-[var(--brand)]">◆</span>
                        {HERO.location}
                    </span>
                </motion.div>
            </div>
        </section>
    );
};
